import axios from "axios";
import { SERVER_URL } from "./config";

const instance = axios.create({
  baseURL: `${SERVER_URL}/api`,
  withCredentials: true,
});

let accessToken: string | null = null;
export function setAccessToken(token: string | null) {
  accessToken = token;
  if (token) {
    instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common.Authorization;
  }
}

let csrfInFlight: Promise<void> | null = null;
let hasCsrf = false;
let csrfFetchedAt = 0;
const CSRF_MAX_AGE = 10 * 60 * 1000; // 10 minutes

async function fetchCsrfOnce(): Promise<void> {
  const { data } = await instance.get("/csrf-token");
  instance.defaults.headers.common["X-CSRF-Token"] = data.csrfToken;
  hasCsrf = true;
  csrfFetchedAt = Date.now();
}

export function getCsrf(force = false): Promise<void> {
  const stale = hasCsrf && (Date.now() - csrfFetchedAt > CSRF_MAX_AGE);
  if (hasCsrf && !force && !stale) return Promise.resolve();
  hasCsrf = false;
  if (!csrfInFlight) {
    csrfInFlight = fetchCsrfOnce()
      .catch(() => {})
      .finally(() => {
        csrfInFlight = null;
      });
  }
  return csrfInFlight || Promise.resolve();
}

instance.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    await getCsrf();
  }
  return config;
});

// Retry on CSRF token errors
instance.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original: any = error.config || {};
    if (
      error.response?.status === 403 &&
      error.response?.data?.msg === "Invalid CSRF token" &&
      !original._csrfRetry
    ) {
      original._csrfRetry = true;
      await getCsrf(true);
      return instance(original);
    }
    throw error;
  }
);


let refreshing = false;
let pending: Array<(t: string | null) => void> = [];

function onRefreshed(token: string | null) {
  pending.forEach((cb) => cb(token));
  pending = [];
}

instance.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original: any = error.config || {};
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !(original.url || "").includes("/auth/refresh-token")
    ) {
      original._retry = true;
      if (refreshing) {
        return new Promise((resolve, reject) => {
          pending.push((t) => {
            if (!t) return reject(error);
            original.headers = original.headers || {};
            original.headers.Authorization = "Bearer " + t;
            resolve(instance(original));
          });
        });
      }
      refreshing = true;
      try {
        const res = await instance.post("/auth/refresh-token");
        const newAccess = res.data.accessToken;
        setAccessToken(newAccess);
        onRefreshed(newAccess);
        original.headers = original.headers || {};
        original.headers.Authorization = "Bearer " + newAccess;
        return instance(original);
      } catch (e) {
        setAccessToken(null);
        onRefreshed(null);
        throw e;
      } finally {
        refreshing = false;
      }
    }
    throw error;
  }
);

export default instance;
